import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { img } from "../../constants/assets";
import { resetPassword } from "../../services/apiService";
import LanguageSwitcher from "../../components/LanguageSwitcher";

function PasswordToggle({ visible, onToggle, t }) {
  return (
    <button
      type="button"
      className="auth-password-toggle"
      onClick={onToggle}
      aria-label={visible ? t("auth.hidePassword") : t("auth.showPassword")}
    >
      {visible ? (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <circle
            cx="12"
            cy="12"
            r="3"
            stroke="currentColor"
            strokeWidth="1.5"
          />
        </svg>
      ) : (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M3 3l18 18"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <path
            d="M10.58 10.58A3 3 0 0 0 12 15a3 3 0 0 0 2.42-4.42M9.88 4.24A10.94 10.94 0 0 1 12 4c6.5 0 10 7 10 7a18.36 18.36 0 0 1-2.16 3.19M6.61 6.61A18.5 18.5 0 0 0 2 12s3.5 7 10 7a9.86 9.86 0 0 0 4.39-1"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      )}
    </button>
  );
}

function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token")?.trim() || "";

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: null, message: "" });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ type: null, message: "" });

    if (!token) {
      setStatus({
        type: "error",
        message: t("auth.invalidResetLink"),
      });
      return;
    }

    const formData = new FormData(event.target);
    const password = formData.get("password")?.trim();
    const confirmPassword = formData.get("confirmPassword")?.trim();

    if (!password) {
      setStatus({ type: "error", message: t("auth.enterNewPassword") });
      return;
    }

    if (password.length < 8) {
      setStatus({
        type: "error",
        message: t("auth.passwordMinLength"),
      });
      return;
    }

    if (password !== confirmPassword) {
      setStatus({ type: "error", message: t("auth.passwordsNoMatch") });
      return;
    }

    setIsSubmitting(true);

    try {
      await resetPassword({ token, password });
      setStatus({
        type: "success",
        message: t("auth.resetSuccess"),
      });
      setTimeout(() => navigate("/login"), 2000);
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error.response?.data?.message ||
          error.message ||
          t("auth.resetFailed"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-lang-switcher-wrap">
        <LanguageSwitcher variant="auth" />
      </div>
      <div className="auth-card">
        <div className="auth-form-panel">
          <div className="auth-form-inner">
            <Link className="auth-logo" to="/">
              <img src={img("logo.svg")} alt="R4L Solutions" />
            </Link>

            <h1 className="auth-title">{t("auth.resetTitle")}</h1>
            <p className="auth-subtitle">{t("auth.resetSubtitle")}</p>

            <form className="auth-form" onSubmit={handleSubmit}>
              <div className="auth-field auth-field--password">
                <input
                  id="reset-password-new"
                  name="password"
                  type={showNewPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder=" "
                  required
                  disabled={isSubmitting}
                />
                <label htmlFor="reset-password-new">{t("auth.newPassword")}</label>
                <PasswordToggle
                  visible={showNewPassword}
                  onToggle={() => setShowNewPassword((visible) => !visible)}
                  t={t}
                />
              </div>

              <div className="auth-field auth-field--password">
                <input
                  id="reset-password-confirm"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder=" "
                  required
                  disabled={isSubmitting}
                />
                <label htmlFor="reset-password-confirm">
                  {t("auth.confirmPassword")}
                </label>
                <PasswordToggle
                  visible={showConfirmPassword}
                  onToggle={() =>
                    setShowConfirmPassword((visible) => !visible)
                  }
                  t={t}
                />
              </div>

              <button
                type="submit"
                className="auth-submit"
                disabled={isSubmitting || !token}
              >
                {isSubmitting ? t("auth.resetting") : t("auth.resetPassword")}
              </button>

              {status.message ? (
                <p
                  className={`auth-form-status auth-form-status--${status.type}`}
                  role="alert"
                >
                  {status.message}
                </p>
              ) : null}

              {!token ? (
                <p className="auth-form-status auth-form-status--error" role="alert">
                  {t("auth.invalidResetLink")}
                </p>
              ) : null}
            </form>
          </div>
        </div>

        <div className="auth-image-panel">
          <div className="auth-image-wrap">
            <img
              src={img("loginRegister.png")}
              alt={t("auth.authImageAlt")}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
